import type { Job } from "../types/job";

const SAMPLE_VIDEO_URL =
  "https://cloud.aideapro.ai/middlewareblobdev/organizations/616de8ec-b1cc-499e-a40c-df1546e4d94c/user/5c5cec7e-e682-4bc4-8a0f-8342c24737aa/ai-generated/AI_Staged_1777971823098.mp4";

const SAMPLE_IMAGE_URL =
  "https://cloud.aideapro.ai/middlewareblobdev/organizations/616de8ec-b1cc-499e-a40c-df1546e4d94c/training/cfebddc3-db16-48c0-a332-2ad7f9a5e9d6/ai-generated/AI_Infographic_1778009270856_1.jpg";

const SAMPLE_WEBP_URL =
  "https://cloud.aideapro.ai/middlewareblob/organizations/616de8ec-b1cc-499e-a40c-df1546e4d94c/training/b4c93221-9c45-4371-a9ff-3c55b132efa7/ai-generated/video_438f7fea-87fe-437d-9e9f-3425b9397849.webp";

const SAMPLE_GIF_URL =
  "https://cloud.aideapro.ai/middlewareblob/organizations/616de8ec-b1cc-499e-a40c-df1546e4d94c/training/d064a596-23c8-4c63-af1b-16bf0c926c4d/ai-generated/video_d4d639b1-f557-490a-897a-78af421eef2a.gif";

const NOW = new Date().toISOString();
const USER_ID = "5c5cec7e-e682-4bc4-8a0f-8342c24737aa";
const TRAINING_ID = "326220f5-3a28-4508-9271-99c430ea88af";

const base = (over: Partial<Job> & { id: string; type: string; status: string }): Job => ({
  approved: false,
  createdAt: NOW,
  updatedAt: NOW,
  userId: USER_ID,
  trainingId: TRAINING_ID,
  name: null,
  description: null,
  model: null,
  error: null,
  navId: null,
  boxId: null,
  sourceUrl: null,
  resultUrl: null,
  sceneIndex: null,
  ...over,
});

export const buildMockJobs = (): Job[] => [
  base({
    id: "mock-staged-init-inspect",
    type: "staged-video-init",
    name: "Storyboard",
    status: "inspect",
    prompt: "Generate storyboard for tutorial intro.",
    result: {
      segments: [
        { sceneIndex: 1, text: "Opening title" },
        { sceneIndex: 2, text: "Topic overview" },
        { sceneIndex: 3, text: "Closing remark" },
      ],
    },
  }),
  base({
    id: "mock-staged-init-pending",
    type: "staged-video-init",
    name: "Storyboard",
    status: "pending",
    prompt: "Generate storyboard for product demo.",
    progress: 20,
  }),

  base({
    id: "mock-staged-gen-inspect",
    type: "staged-video-generation",
    name: "Scene Generation",
    status: "inspect",
    sceneIndex: "1",
    model: "kling-1.6",
    prompt: "Render scene 1: introduction.",
    resultUrl: SAMPLE_VIDEO_URL,
    result: { videoUrl: SAMPLE_VIDEO_URL },
  }),
  base({
    id: "mock-staged-gen-pending",
    type: "staged-video-generation",
    name: "Scene Generation",
    status: "processing",
    sceneIndex: "2",
    model: "kling-1.6",
    prompt: "Render scene 2: main content.",
    progress: 60,
  }),

  base({
    id: "mock-video-export-inspect",
    type: "training-video-export",
    name: "Training Video Export",
    status: "inspect",
    resultUrl: SAMPLE_VIDEO_URL,
    result: { videoUrl: SAMPLE_VIDEO_URL },
  }),
  base({
    id: "mock-video-export-pending",
    type: "training-video-export",
    name: "Training Video Export",
    status: "processing",
    progress: 70,
  }),

  base({
    id: "mock-tts-avatar-inspect",
    type: "tts-avatar",
    name: "TTS Avatar",
    status: "inspect",
    sceneIndex: "1",
    sourceUrl: SAMPLE_IMAGE_URL,
    resultUrl: SAMPLE_VIDEO_URL,
    metadata: { avatarImageUrl: SAMPLE_IMAGE_URL, narration_text: "Hello, welcome to the demo." },
    result: { videoUrl: SAMPLE_VIDEO_URL },
  }),
  base({
    id: "mock-tts-avatar-pending",
    type: "tts-avatar",
    name: "TTS Avatar",
    status: "processing",
    sceneIndex: "2",
    sourceUrl: SAMPLE_IMAGE_URL,
    metadata: { avatarImageUrl: SAMPLE_IMAGE_URL, narration_text: "Generating avatar speech." },
    progress: 35,
  }),

  base({
    id: "mock-tavus-video-inspect",
    type: "tavus-video",
    name: "Tavus Video",
    status: "inspect",
    sceneIndex: "1",
    sourceUrl: SAMPLE_IMAGE_URL,
    resultUrl: SAMPLE_VIDEO_URL,
    metadata: {
      avatarImageUrl: SAMPLE_IMAGE_URL,
      narration_text: "Tavus replica delivering the script.",
    },
    result: { videoUrl: SAMPLE_VIDEO_URL, replicaId: "r_abc123" },
  }),
  base({
    id: "mock-tavus-video-pending",
    type: "tavus-video",
    name: "Tavus Video",
    status: "processing",
    sceneIndex: "2",
    sourceUrl: SAMPLE_IMAGE_URL,
    metadata: { avatarImageUrl: SAMPLE_IMAGE_URL, narration_text: "Rendering Tavus replica." },
    progress: 25,
  }),

  base({
    id: "mock-lipsync-inspect",
    type: "lipsync",
    name: "Lipsync",
    status: "inspect",
    sceneIndex: "1",
    sourceUrl: SAMPLE_IMAGE_URL,
    resultUrl: SAMPLE_VIDEO_URL,
    metadata: {
      avatarImageUrl: SAMPLE_IMAGE_URL,
      narration_text: "Synced lips for the avatar speech.",
    },
    result: { videoUrl: SAMPLE_VIDEO_URL },
  }),
  base({
    id: "mock-lipsync-pending",
    type: "lipsync",
    name: "Lipsync",
    status: "processing",
    sceneIndex: "2",
    sourceUrl: SAMPLE_IMAGE_URL,
    metadata: {
      avatarImageUrl: SAMPLE_IMAGE_URL,
      narration_text: "Aligning audio to mouth movement.",
    },
    progress: 65,
  }),

  base({
    id: "mock-azure-avatar-inspect",
    type: "azure-avatar",
    name: "Azure Avatar",
    status: "inspect",
    sceneIndex: "1",
    sourceUrl: SAMPLE_IMAGE_URL,
    resultUrl: SAMPLE_VIDEO_URL,
    metadata: {
      avatarImageUrl: SAMPLE_IMAGE_URL,
      narration_text: "Azure neural voice narrating the scene.",
    },
    result: { videoUrl: SAMPLE_VIDEO_URL },
  }),
  base({
    id: "mock-azure-avatar-pending",
    type: "azure-avatar",
    name: "Azure Avatar",
    status: "processing",
    sceneIndex: "2",
    sourceUrl: SAMPLE_IMAGE_URL,
    metadata: {
      avatarImageUrl: SAMPLE_IMAGE_URL,
      narration_text: "Synthesizing Azure avatar speech.",
    },
    progress: 50,
  }),

  base({
    id: "mock-image-to-avatar-inspect",
    type: "image-to-avatar",
    name: "Image To Avatar",
    status: "inspect",
    sceneIndex: "1",
    sourceUrl: SAMPLE_IMAGE_URL,
    resultUrl: SAMPLE_VIDEO_URL,
    metadata: { avatarImageUrl: SAMPLE_IMAGE_URL },
    result: { videoUrl: SAMPLE_VIDEO_URL },
  }),
  base({
    id: "mock-image-to-avatar-pending",
    type: "image-to-avatar",
    name: "Image To Avatar",
    status: "pending",
    sceneIndex: "2",
    sourceUrl: SAMPLE_IMAGE_URL,
    metadata: { avatarImageUrl: SAMPLE_IMAGE_URL },
    progress: 10,
  }),

  base({
    id: "mock-image-gen-inspect",
    type: "image-generation",
    name: "Image Generation",
    status: "inspect",
    sceneIndex: "1",
    model: "sdxl-1.0",
    prompt: "A futuristic city skyline at dusk.",
    resultUrl: SAMPLE_WEBP_URL,
  }),
  base({
    id: "mock-image-gen-pending",
    type: "image-generation",
    name: "Image Generation",
    status: "processing",
    sceneIndex: "2",
    model: "sdxl-1.0",
    prompt: "An abstract painting in pastel tones.",
    progress: 50,
  }),

  base({
    id: "mock-merlin-animator-inspect",
    type: "merlin-image-animator",
    name: "Merlin Image Animator",
    status: "inspect",
    sceneIndex: "1",
    model: "merlin-1.0",
    prompt: "Animate the cover illustration with subtle motion.",
    resultUrl: SAMPLE_GIF_URL,
  }),
  base({
    id: "mock-merlin-animator-pending",
    type: "merlin-image-animator",
    name: "Merlin Image Animator",
    status: "processing",
    sceneIndex: "2",
    model: "merlin-1.0",
    prompt: "Animate the chapter banner with parallax effect.",
    progress: 40,
  }),

  base({
    id: "mock-aspect-conv-inspect",
    type: "training-aspect-conversion",
    name: "Training Aspect Conversion",
    status: "inspect",
    prompt: "Convert all training images to 16:9",
  }),
  base({
    id: "mock-aspect-conv-pending",
    type: "training-aspect-conversion",
    name: "Training Aspect Conversion",
    status: "processing",
    prompt: "Convert all training images to 9:16",
    progress: 30,
  }),

  base({
    id: "mock-subtitle-inspect",
    type: "subtitle-generation",
    name: "Subtitle Generation",
    status: "inspect",
    prompt: "Generate subtitles in en",
    metadata: { status: "completed", progress: 100 },
  }),
  base({
    id: "mock-subtitle-pending",
    type: "subtitle-generation",
    name: "Subtitle Generation",
    status: "processing",
    prompt: "Generate subtitles in tr",
    progress: 55,
  }),
];

export const mockJobs: Job[] = buildMockJobs();

export const buildMockHistoryJobs = (): Job[] => {
  const past = new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString();
  return [
    base({
      id: "mock-history-staged-init",
      type: "staged-video-init",
      name: "Storyboard",
      status: "approved",
      prompt: "Storyboard for onboarding video.",
      result: {
        segments: [
          { sceneIndex: 1, text: "Welcome scene" },
          { sceneIndex: 2, text: "Feature highlights" },
        ],
      },
      approved: true,
      updatedAt: past,
    }),
    base({
      id: "mock-history-staged-gen",
      type: "staged-video-generation",
      name: "Scene Generation",
      status: "declined",
      sceneIndex: "3",
      model: "kling-1.6",
      prompt: "Closing scene render.",
      resultUrl: SAMPLE_VIDEO_URL,
      deletedAt: past,
      updatedAt: past,
    }),
    base({
      id: "mock-history-video-export",
      type: "training-video-export",
      name: "Training Video Export",
      status: "approved",
      resultUrl: SAMPLE_VIDEO_URL,
      result: { videoUrl: SAMPLE_VIDEO_URL },
      approved: true,
      updatedAt: past,
    }),
    base({
      id: "mock-history-tts-avatar",
      type: "tts-avatar",
      name: "TTS Avatar",
      status: "approved",
      sceneIndex: "1",
      sourceUrl: SAMPLE_IMAGE_URL,
      resultUrl: SAMPLE_VIDEO_URL,
      metadata: { avatarImageUrl: SAMPLE_IMAGE_URL, narration_text: "Previous avatar narration." },
      result: { videoUrl: SAMPLE_VIDEO_URL },
      approved: true,
      updatedAt: past,
    }),
    base({
      id: "mock-history-tavus-video",
      type: "tavus-video",
      name: "Tavus Video",
      status: "approved",
      sceneIndex: "1",
      sourceUrl: SAMPLE_IMAGE_URL,
      resultUrl: SAMPLE_VIDEO_URL,
      metadata: { avatarImageUrl: SAMPLE_IMAGE_URL, narration_text: "Earlier Tavus delivery." },
      result: { videoUrl: SAMPLE_VIDEO_URL, replicaId: "r_abc123" },
      approved: true,
      updatedAt: past,
    }),
    base({
      id: "mock-history-lipsync",
      type: "lipsync",
      name: "Lipsync",
      status: "declined",
      sceneIndex: "2",
      sourceUrl: SAMPLE_IMAGE_URL,
      resultUrl: SAMPLE_VIDEO_URL,
      metadata: { avatarImageUrl: SAMPLE_IMAGE_URL, narration_text: "Rejected lipsync take." },
      result: { videoUrl: SAMPLE_VIDEO_URL },
      deletedAt: past,
      updatedAt: past,
    }),
    base({
      id: "mock-history-azure-avatar",
      type: "azure-avatar",
      name: "Azure Avatar",
      status: "approved",
      sceneIndex: "1",
      sourceUrl: SAMPLE_IMAGE_URL,
      resultUrl: SAMPLE_VIDEO_URL,
      metadata: {
        avatarImageUrl: SAMPLE_IMAGE_URL,
        narration_text: "Earlier Azure avatar render.",
      },
      result: { videoUrl: SAMPLE_VIDEO_URL },
      approved: true,
      updatedAt: past,
    }),
    base({
      id: "mock-history-image-to-avatar",
      type: "image-to-avatar",
      name: "Image To Avatar",
      status: "declined",
      sceneIndex: "2",
      sourceUrl: SAMPLE_IMAGE_URL,
      metadata: { avatarImageUrl: SAMPLE_IMAGE_URL },
      deletedAt: past,
      updatedAt: past,
    }),
    base({
      id: "mock-history-image-gen",
      type: "image-generation",
      name: "Image Generation",
      status: "approved",
      sceneIndex: "1",
      model: "sdxl-1.0",
      prompt: "Sunset over the ocean.",
      resultUrl: SAMPLE_IMAGE_URL,
      approved: true,
      updatedAt: past,
    }),
    base({
      id: "mock-history-merlin-animator",
      type: "merlin-image-animator",
      name: "Merlin Image Animator",
      status: "approved",
      sceneIndex: "2",
      model: "merlin-1.0",
      prompt: "Animated chapter banner.",
      resultUrl: SAMPLE_GIF_URL,
      approved: true,
      updatedAt: past,
    }),
    base({
      id: "mock-history-aspect-conv",
      type: "training-aspect-conversion",
      name: "Training Aspect Conversion",
      status: "approved",
      prompt: "Convert all training images to 1:1",
      approved: true,
      updatedAt: past,
    }),
    base({
      id: "mock-history-subtitle",
      type: "subtitle-generation",
      name: "Subtitle Generation",
      status: "approved",
      prompt: "Generate subtitles in en",
      approved: true,
      updatedAt: past,
    }),
  ];
};
